import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  User,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../utils/firebase';

interface AppUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  [key: string]: any; // Allow for additional Firestore user data
}

interface AuthContextType {
  user: AppUser | null;
  loading: boolean;
  initialized: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const googleProvider = new GoogleAuthProvider();

  // Handle auth state changes
  useEffect(() => {
    console.log('AuthContext - Setting up auth state listener');
    let unsubscribed = false;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('AuthContext - Auth state changed:', {
        hasUser: !!firebaseUser,
        currentUid: firebaseUser?.uid,
        currentTime: new Date().toISOString()
      });

      if (unsubscribed) return;

      // If no user, immediately set states and return
      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        setInitialized(true);
        return;
      }

      // Only fetch Firestore data if we have a logged-in user
      try {
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        const userData = userDoc.data();
        
        if (!unsubscribed) {
          const appUser: AppUser = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || '',
            photoURL: firebaseUser.photoURL || null,
            ...userData
          };
          setUser(appUser);
        }
      } catch (error) {
        console.error('AuthContext - Error in auth state change:', error);
        if (!unsubscribed) {
          setUser(null);
        }
      } finally {
        if (!unsubscribed) {
          setLoading(false);
          setInitialized(true);
        }
      }
    });

    return () => {
      console.log('AuthContext - Cleaning up auth state listener');
      unsubscribed = true;
      unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      setLoading(true);
      console.log('AuthContext - Attempting login');
      await signInWithEmailAndPassword(auth, email, password);
      // Don't set loading to false here - let the auth state change handler do it
    } catch (error) {
      console.error('AuthContext - Login error:', error);
      setLoading(false);
      throw error;
    }
  };

  const register = async (email: string, password: string, displayName: string): Promise<void> => {
    try {
      setLoading(true);
      console.log('AuthContext - Attempting registration');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Update display name
      await updateProfile(firebaseUser, { displayName });

      // Create user document in Firestore
      const userData: Omit<AppUser, 'uid'> = {
        email: firebaseUser.email || '',
        displayName: displayName || '',
        photoURL: firebaseUser.photoURL || null,
        createdAt: serverTimestamp(),
      };

      await setDoc(doc(db, 'users', firebaseUser.uid), userData);
      // Don't set loading to false here - let the auth state change handler do it
    } catch (error) {
      console.error('AuthContext - Registration error:', error);
      setLoading(false);
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      setLoading(true);
      console.log('AuthContext - Attempting sign out');
      await firebaseSignOut(auth);
      // Don't set loading to false here - let the auth state change handler do it
    } catch (error) {
      console.error('AuthContext - Sign out error:', error);
      setLoading(false);
      throw error;
    }
  };

  const signInWithGoogle = async (): Promise<void> => {
    try {
      setLoading(true);
      console.log('AuthContext - Attempting Google sign in');
      const result = await signInWithPopup(auth, googleProvider);
      
      await setDoc(doc(db, 'users', result.user.uid), {
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        lastSignIn: serverTimestamp(),
      }, { merge: true });
      // Don't set loading to false here - let the auth state change handler do it
    } catch (error) {
      console.error('AuthContext - Google sign in error:', error);
      setLoading(false);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    initialized,
    login,
    register,
    signOut,
    signInWithGoogle,
  };

  console.log('AuthContext - Current state:', {
    hasUser: !!user,
    loading,
    initialized,
    path: window.location.pathname,
    currentTime: new Date().toISOString()
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 